'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { EditorImageDialog } from '@/components/ui/editor-image-dialog';
import type { MDXEditorMethods } from '@mdxeditor/editor';
type EditorEngine = 'mdx' | 'legacy';

/** Read once at module load — avoids re-evaluating on every render */
const EDITOR_ENGINE: EditorEngine = process.env.NEXT_PUBLIC_EDITOR_ENGINE as EditorEngine || 'legacy';

// ─── Shared prop interface (superset of both editors) ────────────────────────
export interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Show a minimal toolbar (bold/italic/links/lists only). Only applies to MDX engine. */
  minimal?: boolean;
  className?: string;
  readOnly?: boolean;
  /** Override the height (applies to legacy editor; MDX auto-sizes) */
  height?: number;
  /** Override the engine for this specific instance. Falls back to NEXT_PUBLIC_EDITOR_ENGINE env var. */
  engine?: EditorEngine;
  /** Enable image insertion toolbar (upload, library, URL). Default: true */
  enableImageInsert?: boolean;
}

// ─── Legacy editor (react-md-editor) loaded dynamically ──────────────────────
const LegacyEditor = dynamic(() => import('@uiw/react-md-editor'), {
  ssr: false
});

// Import commands separately so we can access getExtraCommands
const legacyCommandsPromise = import('@uiw/react-md-editor').then(mod => mod.commands);
function LegacyWrapper(t0) {
  const $ = _c(28);
  const {
    value,
    onChange,
    placeholder,
    height: t1,
    readOnly,
    enableImageInsert: t2
  } = t0;
  const height = t1 === undefined ? 200 : t1;
  const enableImageInsert = t2 === undefined ? true : t2;
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [extraCmds, setExtraCmds] = useState(undefined);
  const cursorPosRef = useRef(null);
  let t3;
  if ($[0] !== enableImageInsert || $[1] !== readOnly) {
    t3 = () => {
      legacyCommandsPromise.then(commands => {
        const defaults = commands.getExtraCommands();
        if (enableImageInsert && !readOnly) {
          const imageCmd = {
            name: "insert-image",
            keyCommand: "insert-image",
            buttonProps: {
              "aria-label": "Insert image",
              title: "Insert image"
            },
            icon: <svg width="13" height="13" viewBox="0 0 20 20"><path fill="currentColor" d="M15 9c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4-7H1c-.55 0-1 .45-1 1v14c0 .55.45 1 1 1h18c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm-1 13l-6-5-2 2-4-5-4 8V4h16v11z" /></svg>,
            execute: () => {
              setImageDialogOpen(true);
            }
          };
          setExtraCmds([imageCmd, commands.divider, ...defaults]);
        } else {
          setExtraCmds(defaults);
        }
      });
    };
    $[0] = enableImageInsert;
    $[1] = readOnly;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useState(t3);
  let t4;
  if ($[3] !== onChange || $[4] !== value) {
    t4 = (url, alt) => {
      const markdownImage = `![${alt || "image"}](${url})`;
      const current = value || "";
      const pos = cursorPosRef.current;
      if (pos !== null && pos <= current.length) {
        const before = current.slice(0, pos);
        const after = current.slice(pos);
        const needsNewlineBefore = before.length > 0 && !before.endsWith("\n");
        const needsNewlineAfter = after.length > 0 && !after.startsWith("\n");
        const inserted = `${needsNewlineBefore ? "\n" : ""}${markdownImage}${needsNewlineAfter ? "\n" : ""}`;
        onChange?.(before + inserted + after);
      } else {
        const newValue = current ? `${current}\n\n${markdownImage}` : markdownImage;
        onChange?.(newValue);
      }
    };
    $[3] = onChange;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleImageInsert = t4;
  const t5 = value || "";
  let t6;
  if ($[6] !== onChange) {
    t6 = v => onChange?.(v || "");
    $[6] = onChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = placeholder || "Write content... (supports markdown)";
  let t10;
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => {
      const textarea = e.target as HTMLTextAreaElement;
      cursorPosRef.current = textarea.selectionStart;
    };
    t9 = e_0 => {
      const textarea_0 = e_0.target as HTMLTextAreaElement;
      cursorPosRef.current = textarea_0.selectionStart;
    };
    t10 = e_1 => {
      const textarea_1 = e_1.target as HTMLTextAreaElement;
      cursorPosRef.current = textarea_1.selectionStart;
    };
    $[8] = t10;
    $[9] = t8;
    $[10] = t9;
  } else {
    t10 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] !== t7) {
    t11 = {
      placeholder: t7,
      onSelect: t8,
      onClick: t9,
      onKeyUp: t10
    };
    $[11] = t7;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== extraCmds || $[14] !== height || $[15] !== readOnly || $[16] !== t11 || $[17] !== t5 || $[18] !== t6) {
    t12 = <div data-color-mode="light"><LegacyEditor value={t5} onChange={t6} preview="live" height={height} textareaProps={t11} hideToolbar={readOnly} extraCommands={extraCmds} /></div>;
    $[13] = extraCmds;
    $[14] = height;
    $[15] = readOnly;
    $[16] = t11;
    $[17] = t5;
    $[18] = t6;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== enableImageInsert || $[21] !== handleImageInsert || $[22] !== imageDialogOpen || $[23] !== readOnly) {
    t13 = enableImageInsert && !readOnly && <EditorImageDialog open={imageDialogOpen} onOpenChange={setImageDialogOpen} onInsert={handleImageInsert} />;
    $[20] = enableImageInsert;
    $[21] = handleImageInsert;
    $[22] = imageDialogOpen;
    $[23] = readOnly;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}

// ─── MDX editor loaded dynamically ──────────────────────────────────────────
const MDXEditorLazy = dynamic(() => import('@/components/ui/mdx-editor').then(mod => ({
  default: mod.MarkdownEditor
})), {
  ssr: false,
  loading: () => <div className="animate-pulse rounded-md border bg-muted/40 min-h-[200px]" />
});
function MDXWrapper(t0) {
  const $ = _c(20);
  const {
    value,
    onChange,
    placeholder,
    minimal,
    className,
    readOnly,
    enableImageInsert: t1
  } = t0;
  const enableImageInsert = t1 === undefined ? true : t1;
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const editorRef = useRef(null);
  let t2;
  if ($[0] !== onChange || $[1] !== value) {
    t2 = (url, alt) => {
      const markdownImage = `![${alt || "image"}](${url})`;
      if (editorRef.current) {
        editorRef.current.insertMarkdown(markdownImage);
      } else {
        const current = value || "";
        const newValue = current ? `${current}\n\n${markdownImage}` : markdownImage;
        onChange?.(newValue);
      }
    };
    $[0] = onChange;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleImageInsert = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setImageDialogOpen(true);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleInsertImageClick = t3;
  const t4 = enableImageInsert && !readOnly ? handleInsertImageClick : undefined;
  let t5;
  if ($[4] !== className || $[5] !== minimal || $[6] !== onChange || $[7] !== placeholder || $[8] !== readOnly || $[9] !== t4 || $[10] !== value) {
    t5 = <MDXEditorLazy ref={editorRef} value={value} onChange={onChange} placeholder={placeholder} minimal={minimal} className={className} readOnly={readOnly} onInsertImageClick={t4} />;
    $[4] = className;
    $[5] = minimal;
    $[6] = onChange;
    $[7] = placeholder;
    $[8] = readOnly;
    $[9] = t4;
    $[10] = value;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== enableImageInsert || $[13] !== handleImageInsert || $[14] !== imageDialogOpen || $[15] !== readOnly) {
    t6 = enableImageInsert && !readOnly && <EditorImageDialog open={imageDialogOpen} onOpenChange={setImageDialogOpen} onInsert={handleImageInsert} />;
    $[12] = enableImageInsert;
    $[13] = handleImageInsert;
    $[14] = imageDialogOpen;
    $[15] = readOnly;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

// ─── Unified component ───────────────────────────────────────────────────────
/**
 * A unified rich text / markdown editor that delegates to either:
 * - `@mdxeditor/editor` (WYSIWYG, full plugin support)
 * - `@uiw/react-md-editor` (legacy split-pane editor)
 *
 * Controlled via `NEXT_PUBLIC_EDITOR_ENGINE` env var:
 * - `"mdx"` → new MDXEditor
 * - `"legacy"` (default) → old react-md-editor
 *
 * Image insertion (upload, library browse, URL) is integrated into the toolbar
 * and inserts at the current cursor position.
 *
 * @example
 * ```tsx
 * import { RichTextEditor } from '@/components/ui/rich-text-editor';
 *
 * <RichTextEditor value={content} onChange={setContent} />
 * ```
 */
export function RichTextEditor(t0) {
  const $ = _c(7);
  let engine;
  let props;
  if ($[0] !== t0) {
    ({
      engine,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = engine;
    $[2] = props;
  } else {
    engine = $[1];
    props = $[2];
  }
  const resolvedEngine = engine ?? EDITOR_ENGINE;
  if (resolvedEngine === "mdx") {
    let t1;
    if ($[3] !== props) {
      t1 = <MDXWrapper {...props} />;
      $[3] = props;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== props) {
    t1 = <LegacyWrapper {...props} />;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}